import { DISPLAY_CHORD_REGEX, isChordLine, isLyricsLine } from './chordPatterns';
import { applyRepeatSlashes, parseRepeatDirective, stripManualRepeatSlashes } from './repeat';

const SKIPPED_PARTS = ['Intro', 'Music', 'Instrumental', 'Outro', 'Note', 'Hidden', 'Section'];

const VERSE_PREFIX_MAP = {
  Verse: 'v',
  Chorus: 'c',
  'Pre-chorus': 'p',
  Bridge: 'b',
  Refrain: 'o',
  Tag: 'o',
};

// All off: the plain original parts, with nothing the editor adds around them
export const DEFAULT_XML_EXPORT_OPTIONS = {
  repeatSlashes: false,
  writeDuplicates: false,
  chords: false,
  comments: false,
};

const LINE_BREAK = /\r\n|\r|\n/;

// A chord rides through the line's cleanup as a private-use token, so no marker or escape can
// touch it, and becomes a <chord/> tag at the very end
const CHORD_TOKEN = /(\d+)/g;
const chordToken = (i) => `${i}`;

function escapeXml(str) {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

// Each chord on the line above goes into the lyric at the chord's column, padding the lyric out
// for a chord that hangs past its end. Right to left, so the columns still to come don't move.
const insertChords = (chordLine, lyricLine, chords) => {
  const matches = [...chordLine.matchAll(DISPLAY_CHORD_REGEX)];
  let line = lyricLine.padEnd(Math.max(0, ...matches.map((m) => m.index)));

  for (let i = matches.length - 1; i >= 0; i--) {
    const { index } = matches[i];
    
    chords.push(matches[i][1].replace(/\^\d$/, ''));
    line = line.slice(0, index) + chordToken(chords.length - 1) + line.slice(index);
  }

  return line;
};

// Escaping first is safe: it leaves *, _, [, ] and # alone, so the markers are still there to find
const cleanLine = (line, chords, { comments }) =>
  escapeXml(line)
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/\*(.*?)\*/g, '$1')
    .replace(/__(.*?)__/g, '$1')
    .replace(/\[(.*?)\]|##(.*?)##/g, (matched, bracket, heading) =>
      comments ? `<comment>${(bracket ?? heading).trim()}</comment>` : ' ',
    )
    .replace(/\s+/g, ' ')
    .trim()
    .replace(CHORD_TOKEN, (matched, i) => `<chord name="${escapeXml(chords[i])}"/>`);

// One part's lyrics as the contents of a <lines> element, '' when nothing is left of them
const partLines = (part, options) => {
  const { strippedLyrics } = parseRepeatDirective(part.lyrics || '');
  const marked = options.repeatSlashes
    ? applyRepeatSlashes(strippedLyrics, part.repeatCount, part.name)
    : stripManualRepeatSlashes(strippedLyrics);
  // A ((note)) can run over several lines, so it comes out before the split
  const raw = marked.replace(/\(\([\s\S]*?\)\)/g, '').split(LINE_BREAK);

  const chords = [];
  const mergesDown = (i) => isChordLine(raw[i]) && i + 1 < raw.length && isLyricsLine(raw[i + 1]);

  const lines = raw.flatMap((line, i) => {
    if (isChordLine(line)) {
      // One over a lyric is written into that lyric, when its turn comes
      return options.chords && !mergesDown(i) ? [insertChords(line, '', chords)] : [];
    }

    return [options.chords && i > 0 && mergesDown(i - 1) ? insertChords(raw[i - 1], line, chords) : line];
  });

  return lines
    .map((line) => cleanLine(line, chords, options))
    .filter((line) => line.length > 0)
    .join('<br/>');
};

const sameRepeatCount = (a, b) => (Number(a?.repeatCount) || 1) === (Number(b?.repeatCount) || 1);

export function generateOpenLyricsXml(song, options = {}) {
  const opts = { ...DEFAULT_XML_EXPORT_OPTIONS, ...options };
  const partsById = Object.fromEntries(song.parts.map((part) => [part.id, part]));

  // Build a map from part id → verse name for duplicate resolution
  const verseNameById = {};
  const prefixCounters = {};
  const uniqueVerses = []; // { verseName, lines }
  const verseOrder = [];

  song.parts.forEach((part) => {
    if (SKIPPED_PARTS.includes(part.name)) return;

    if (part.isDuplicate && part.duplicateId && !opts.writeDuplicates) {
      const originalVerseName = verseNameById[part.duplicateId];
      if (!originalVerseName) return;

      // Referencing the original repeats its slashes, which are wrong for a different count
      if (!opts.repeatSlashes || sameRepeatCount(part, partsById[part.duplicateId])) {
        verseOrder.push(originalVerseName);
        return;
      }
    }

    const lines = partLines(part, opts);
    if (!lines) return;

    const prefix = VERSE_PREFIX_MAP[part.name] ?? 'o';
    prefixCounters[prefix] = (prefixCounters[prefix] || 0) + 1;
    const verseName = `${prefix}${prefixCounters[prefix]}`;

    verseNameById[part.id] = verseName;
    verseOrder.push(verseName);
    uniqueVerses.push({ verseName, lines });
  });

  // Only needed when a verse is sung more than once; otherwise the verses are already in order
  const hasRepeatedVerse = new Set(verseOrder).size < verseOrder.length;

  // Build XML
  const indent = '  ';
  const lines = [];

  lines.push(`<?xml version='1.0' encoding='UTF-8'?>`);
  lines.push(`<song xmlns="http://openlyrics.info/namespace/2009/song" version="0.9" createdIn="Migrupo">`);

  // <properties>
  lines.push(`${indent}<properties>`);
  lines.push(`${indent}${indent}<titles>`);
  lines.push(`${indent}${indent}${indent}<title>${escapeXml(song.title)}</title>`);
  lines.push(`${indent}${indent}</titles>`);

  if (song.artist) {
    lines.push(`${indent}${indent}<authors>`);
    lines.push(`${indent}${indent}${indent}<author>${escapeXml(song.artist)}</author>`);
    lines.push(`${indent}${indent}</authors>`);
  }

  if (song.key) {
    lines.push(`${indent}${indent}<key>${escapeXml(song.key)}</key>`);
  }

  if (song.bpm) {
    lines.push(`${indent}${indent}<tempo type="bpm">${escapeXml(String(song.bpm))}</tempo>`);
  }

  if (song.timeSignature) {
    lines.push(`${indent}${indent}<timeSignature>${escapeXml(song.timeSignature)}</timeSignature>`);
  }

  if (hasRepeatedVerse) {
    lines.push(`${indent}${indent}<verseOrder>${verseOrder.join(' ')}</verseOrder>`);
  }

  lines.push(`${indent}</properties>`);

  // <lyrics>
  lines.push(`${indent}<lyrics>`);
  uniqueVerses.forEach(({ verseName, lines: verseLines }) => {
    lines.push(`${indent}${indent}<verse name="${verseName}">`);
    lines.push(`${indent}${indent}${indent}<lines>${verseLines}</lines>`);
    lines.push(`${indent}${indent}</verse>`);
  });
  lines.push(`${indent}</lyrics>`);

  lines.push(`</song>`);

  return lines.join('\n');
}

export function downloadOpenLyricsFile(song, options) {
  const xml = generateOpenLyricsXml(song, options);
  const blob = new Blob([xml], { type: 'application/xml;charset=utf-8' });
  const url = URL.createObjectURL(blob);

  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `${song.title}.xml`;
  anchor.click();

  URL.revokeObjectURL(url);
}
